// A report's actions, from the capabilities the service computed for this user. An action the user may
// not take is not shown; a user who cannot edit gets the eye icon to the report's page instead.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteAlert from "components/Alerts/Delete";
import { showSnackbar } from "Utilities/Snackbar";
import { classifyReportError } from "utils/reportErrors";
import { useDeleteReportMutation, usePauseReportMutation, useResumeReportMutation } from "services/scheduledReports";

const TITLE = "Scheduled Reports";

export default function ReportActions({ report, showView, onDeleted }) {
  const { enqueueSnackbar } = useSnackbar();
  const [pause, pauseResult] = usePauseReportMutation();
  const [resume, resumeResult] = useResumeReportMutation();
  const [remove, removeResult] = useDeleteReportMutation();
  const [confirming, setConfirming] = useState(false);
  const caps = report.capabilities || {};
  const busy = pauseResult.isLoading || resumeResult.isLoading;

  const act = async (action, doneText) => {
    const res = await action({ token: window.localStorage.getItem("token"), id: report.id });
    if (res.error) {
      showSnackbar(TITLE, classifyReportError(res.error).message, "error", 4000, enqueueSnackbar);
      return false;
    }
    showSnackbar(TITLE, doneText, "success", 2000, enqueueSnackbar);
    return true;
  };

  const onDelete = async () => {
    const done = await act(remove, "Report deleted.");
    setConfirming(false);
    if (done && onDeleted) onDeleted();
  };

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {showView && caps.history ? (
        <Tooltip title="View this report and its history">
          <IconButton size="small" component={Link} to={`/scheduledReports/history/${report.id}`}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {caps.edit ? (
        <Tooltip title="Edit this report">
          <IconButton size="small" component={Link} to={`/scheduledReports/edit/${report.id}`}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {caps.pause ? (
        <Tooltip title="Pause this report">
          <span>
            <IconButton size="small" disabled={busy} onClick={() => act(pause, "Report paused.")}>
              <PauseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
      {caps.resume ? (
        <Tooltip title="Resume this report. Reports missed while it was paused are not sent.">
          <span>
            <IconButton size="small" disabled={busy} onClick={() => act(resume, "Report resumed.")}>
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
      {caps.delete ? (
        <Tooltip title="Delete this report">
          <IconButton size="small" onClick={() => setConfirming(true)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {confirming ? (
        <DeleteAlert
          deleteModal={confirming}
          question={`Delete the report "${report.name}"? It stops being sent. Its earlier runs stay on record until they expire.`}
          handleDelete={onDelete}
          handleClose={() => setConfirming(false)}
          deleteResult={removeResult}
        />
      ) : null}
    </div>
  );
}
